import { type ComponentPropsWithoutRef, memo, type ReactNode } from 'react';

import { cn } from '@air/utils-tailwind';

export type SideNavTrayListItemRootProps = ComponentPropsWithoutRef<'div'> & {
  testId: string;
};

export const SideNavTrayListItemRoot = memo(
  ({ children, className, testId, ...rest }: SideNavTrayListItemRootProps) => (
    <div
      className={cn(
        'group flex w-full items-center justify-between px-4 py-1.5 ads:hover:bg-hover-subtle hover:bg-gray-2',
        className,
      )}
      data-testid={testId}
      {...rest}
    >
      {children}
    </div>
  ),
);

SideNavTrayListItemRoot.displayName = 'SideNavTrayListItemRoot';

export type SideNavTrayListItemContentProps = {
  children: ReactNode;
  dimmed: boolean;
};

export const SideNavTrayListItemContent = memo(({ children, dimmed }: SideNavTrayListItemContentProps) => (
  <div className={cn('flex min-w-0 flex-1 items-center', dimmed && 'opacity-60')}>{children}</div>
));

SideNavTrayListItemContent.displayName = 'SideNavTrayListItemContent';

export type SideNavTrayListItemBodyProps = {
  children: ReactNode;
};

export const SideNavTrayListItemBody = memo(({ children }: SideNavTrayListItemBodyProps) => (
  <div className="flex min-w-0 flex-1 flex-col justify-center">{children}</div>
));

SideNavTrayListItemBody.displayName = 'SideNavTrayListItemBody';

export type SideNavTrayListItemTitleProps = {
  children: ReactNode;
  testId: string;
};

export const SideNavTrayListItemTitle = memo(({ children, testId }: SideNavTrayListItemTitleProps) => (
  <p className="mb-0.5 truncate font-medium ads:text-primary text-12 text-gray-12" data-testid={testId}>
    {children}
  </p>
));

SideNavTrayListItemTitle.displayName = 'SideNavTrayListItemTitle';

export type SideNavTrayListItemStatusProps = {
  children: ReactNode;
  testId: string;
};

export const SideNavTrayListItemStatus = memo(({ children, testId }: SideNavTrayListItemStatusProps) => (
  <p className="truncate font-normal ads:text-metadata text-12 text-gray-10" data-testid={testId}>
    {children}
  </p>
));

SideNavTrayListItemStatus.displayName = 'SideNavTrayListItemStatus';

export type SideNavTrayListItemActionsProps = {
  children: ReactNode;
  /** Fades buttons in on row hover; default is show/hide via display. */
  fadeOnHover?: boolean;
};

export const SideNavTrayListItemActions = memo(({ children, fadeOnHover = false }: SideNavTrayListItemActionsProps) => (
  <div
    className={cn(
      'shrink-0 items-center justify-end gap-2.5 pl-2.5',
      fadeOnHover
        ? 'flex opacity-0 group-focus-within:opacity-100 group-hover:opacity-100'
        : 'hidden group-focus-within:flex group-hover:flex',
    )}
  >
    {children}
  </div>
));

SideNavTrayListItemActions.displayName = 'SideNavTrayListItemActions';
